import { Link } from 'react-router-dom';
import {
  ICP_REPO_ID,
  REPOSITORY_TEAM,
  REPOSITORY_ROOT,
  BACKEND_STRUCTURE,
  BACKEND_DOMAINS,
  FRONTEND_STRUCTURE,
  COMPONENT_LIBRARY_TIERS,
  DATABASE_STRUCTURE,
  POSTGRES_SCHEMAS,
  AI_STRUCTURE,
  MARKETPLACE_STRUCTURE,
  SHARED_LIBRARIES,
  INFRASTRUCTURE,
  DOCUMENTATION,
  TESTING,
  TOOLS,
  CONFIGURATION,
  IMPORT_RULES,
  BUILD_RULES,
  OWNERSHIP_FIELDS,
  NAMING_CONVENTIONS,
  FUTURE_ICP_POINTS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_TEAM,
  DEMO_ROOT,
  DEMO_BACKEND,
  DEMO_FRONTEND,
  DEMO_SCHEMAS,
  DEMO_AI_MARKETPLACE,
  DEMO_SHARED_INFRA,
  DEMO_EXECUTIVE,
  buildRepositoryDashboard,
} from '../../repositoryblueprint/framework';
import {
  RepositoryTeamPanel,
  RepositoryRootPanel,
  BackendDomainPanel,
  FrontendComponentPanel,
  DatabaseSchemaPanel,
  AIMarketplaceStructurePanel,
  SharedInfraConfigPanel,
  ExecutiveRepositoryDashboardPanel,
} from '../../components/repositoryblueprint';
import { Card, KpiCard } from '../../components/ui';

export function RepositoryBlueprintPage() {
  const dashboard = buildRepositoryDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{ICP_REPO_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Repository Blueprint</h1>
        <p className="mt-1 text-sm text-slate-500">
          Official physical monorepo structure — every folder, package, domain, schema, and shared library
          for modular monolith, DDD, event-driven, multi-tenant SaaS evolution.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        <KpiCard label="Team Roles" value={dashboard.team_roles.toString()} />
        <KpiCard label="Root Areas" value={dashboard.root_areas.toString()} />
        <KpiCard label="Backend Domains" value={dashboard.backend_domains.toString()} />
        <KpiCard label="PG Schemas" value={dashboard.postgres_schemas.toString()} />
        <KpiCard label="Shared Libraries" value={dashboard.shared_libraries.toString()} />
        <KpiCard label="Governance Areas" value={dashboard.governance_areas.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Repository Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Repository Architecture Team</h2>
        <p className="mt-1 text-sm text-slate-500">{REPOSITORY_TEAM.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <RepositoryTeamPanel members={DEMO_TEAM} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Repository Root Structure</h2>
        <p className="mt-1 text-sm text-slate-500">{REPOSITORY_ROOT.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <RepositoryRootPanel items={DEMO_ROOT} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Backend Domains & Structure</h2>
        <p className="mt-1 text-sm text-slate-500">{BACKEND_STRUCTURE.slice(0, 8).join(' · ')}…</p>
        <p className="mt-1 text-xs text-slate-400">{BACKEND_DOMAINS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <BackendDomainPanel items={DEMO_BACKEND} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Frontend & Component Library</h2>
        <p className="mt-1 text-sm text-slate-500">{FRONTEND_STRUCTURE.slice(0, 8).join(' · ')}…</p>
        <p className="mt-1 text-xs text-slate-400">{COMPONENT_LIBRARY_TIERS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <FrontendComponentPanel items={DEMO_FRONTEND} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Database & PostgreSQL Schemas</h2>
        <p className="mt-1 text-sm text-slate-500">{DATABASE_STRUCTURE.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{POSTGRES_SCHEMAS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <DatabaseSchemaPanel items={DEMO_SCHEMAS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI & Marketplace Structure</h2>
        <p className="mt-1 text-sm text-slate-500">{AI_STRUCTURE.slice(0, 6).join(' · ')}… · {MARKETPLACE_STRUCTURE.slice(0, 6).join(' · ')}…</p>
        <Card className="mt-4" padding="sm">
          <AIMarketplaceStructurePanel items={DEMO_AI_MARKETPLACE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Shared Libraries, Infrastructure & Configuration</h2>
        <p className="mt-1 text-sm text-slate-500">{SHARED_LIBRARIES.join(' · ')} · {INFRASTRUCTURE.slice(0, 5).join(' · ')}…</p>
        <p className="mt-1 text-xs text-slate-400">{DOCUMENTATION.slice(0, 6).join(' · ')}… · {TESTING.join(' · ')} · {TOOLS.join(' · ')} · {CONFIGURATION.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <SharedInfraConfigPanel items={DEMO_SHARED_INFRA} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Import Rules, Build Rules & Ownership</h2>
        <p className="mt-1 text-sm text-slate-500">{IMPORT_RULES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{BUILD_RULES.join(' · ')} · {OWNERSHIP_FIELDS.join(' · ')} · {NAMING_CONVENTIONS.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">Future ICP: {FUTURE_ICP_POINTS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ExecutiveRepositoryDashboardPanel items={DEMO_EXECUTIVE} />
        </Card>
      </section>
    </div>
  );
}
